import React from 'react';
import { Button } from '@/components/ui/button';

interface State {
  hasError: boolean;
}

/**
 * Error boundary that catches chunk load failures (e.g. lazy route import errors)
 * and shows a branded reload prompt instead of a black screen.
 */
export class LazyErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    // Auto-retry once for chunk load failures
    const isChunkError =
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Failed to fetch') ||
      error.message?.includes('dynamically imported module') ||
      error.name === 'ChunkLoadError';

    if (isChunkError) {
      // Force reload to get fresh chunks
      const reloadKey = 'lazy-error-reload';
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();

      if (!lastReload || now - Number(lastReload) > 10000) {
        sessionStorage.setItem(reloadKey, String(now));
        window.location.reload();
        return;
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-[60vh] bg-background">
          <div className="flex flex-col items-center gap-4 text-center px-4">
            <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <span className="text-destructive text-xl">!</span>
            </div>
            <p className="text-foreground font-display font-bold text-lg">
              Something went wrong
            </p>
            <p className="text-muted-foreground text-sm max-w-xs">
              The page couldn't be loaded. Please try again.
            </p>
            <Button
              onClick={() => window.location.reload()}
              className="mt-2"
            >
              Reload page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
